import { useEffect, useRef } from "react";
import { useAuth } from "./useAuth";
import { useToast } from "./use-toast";
import { generalReportException } from "@/lib/logs/reportExceptions";

export const useAuthErrorHandler = () => {
  const { signOut } = useAuth();
  const { toast } = useToast();
  const isPaymentInProgress = useRef(false);
  const isRpcCallInProgress = useRef(false);
  const activeOperations = useRef(new Set<string>());
  const protectionStartTime = useRef<number | null>(null);

  // Provide methods to control payment and RPC status with enhanced protection
  const setPaymentInProgress = (inProgress: boolean) => {
    if (inProgress && !isPaymentInProgress.current) {
      protectionStartTime.current = Date.now();
    } else if (!inProgress) {
      protectionStartTime.current = null;
    }

    isPaymentInProgress.current = inProgress;
  };

  const setRpcCallInProgress = (inProgress: boolean, operationType?: string) => {
    isRpcCallInProgress.current = inProgress;

    if (operationType) {
      if (inProgress) {
        const operationKey = `${operationType}-${Date.now()}`;
        activeOperations.current.add(operationKey);

        // Extended auto-cleanup to 15 seconds for more protection
        setTimeout(() => {
          activeOperations.current.delete(operationKey);
        }, 15000);
      } else {
        // Remove all operations of this type
        const opsToRemove = Array.from(activeOperations.current).filter(op => op.startsWith(operationType));
        opsToRemove.forEach(op => {
          activeOperations.current.delete(op);
        });
      }
    }
  };

  const hasActiveOperations = () => {
    const hasPayment = isPaymentInProgress.current;
    const hasRpc = isRpcCallInProgress.current;
    const hasActiveOps = activeOperations.current.size > 0;

    return hasPayment || hasRpc || hasActiveOps;
  };

  useEffect(() => {
    // Enhanced auth error handler with stronger protection
    const handleAuthError = (error: any, url: string) => {
      // EXTENDED grace period for auth error checks - 5 seconds
      setTimeout(() => {
        // Enhanced active operations check with detailed logging
        const currentlyActive = hasActiveOperations();

        if (currentlyActive) {
          return;
        }

        // More specific auth error detection
        const isRealAuthError = (
          (error?.status === 401 && error?.message?.includes("JWT expired")) ||
          (error?.code === "PGRST301") ||
          (error?.message?.includes("invalid JWT")) ||
          (error?.message?.includes("token has expired")) ||
          (error?.status === 401 && url.includes("/rpc/"))
        );

        // Only handle real authentication errors
        if (isRealAuthError) {
          toast({
            title: "Oturum Süresi Doldu",
            description: "Güvenliğiniz için oturumunuz kapatıldı. Lütfen tekrar giriş yapın.",
            variant: "destructive",
          });
          signOut();
        }
      }, 8000); // ENHANCED: 8 second grace period (increased from 5)
    };

    // Enhanced global error listener for Supabase requests with stronger protection
    const originalFetch = window.fetch;

    window.fetch = async (...args) => {
      try {
        const response = await originalFetch(...args);
        const url = args[0]?.toString() || "";

        // Enhanced Supabase API call detection
        if (response.status === 401 && url.includes("supabase.co") && url.includes("/rest/v1/")) {
          // ENHANCED check for RPC calls during active operations
          if (url.includes("/rpc/")) {
            const currentlyActive = hasActiveOperations();

            if (currentlyActive) {
              return response;
            }
          }

          // EXTENDED delay before processing auth error
          setTimeout(() => {
            handleAuthError({ status: 401, message: response.statusText }, url);
          }, 3000); // ENHANCED: 3 second delay (increased from 2) before checking if it's a real auth error
        }

        return response;
      } catch (error) {
        generalReportException(error as Error, { operation: "useAuthErrorHandler.fetchError" });
        throw error;
      }
    };

    // Cleanup
    return () => {
      window.fetch = originalFetch;
    };
  }, [signOut, toast]);

  return { setPaymentInProgress, setRpcCallInProgress, hasActiveOperations };
};
